import React from 'react';
import { KoText, KoTag, KoDateFormatter } from 'packages';

// ── HEALTH STATUS ───────────────────────────────────────────────
export const HEALTH_STATUS = {
  HEALTHY: 'healthy',
  DEGRADED: 'degraded',
  CRITICAL: 'critical'
};

export const HEALTH_STATUS_CONFIG = {
  healthy: { color: 'success', label: 'Healthy', dotColor: '#52c41a' },
  degraded: { color: 'warning', label: 'Degraded', dotColor: '#faad14' },
  critical: { color: 'error', label: 'Critical', dotColor: '#f5222d' }
};

// ── ALERT SEVERITY ──────────────────────────────────────────────
export const ALERT_SEVERITY = {
  CRITICAL: 'critical',
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
  INFO: 'info'
};

export const ALERT_SEVERITY_CONFIG = {
  critical: { color: 'error', label: 'Critical' },
  high: { color: 'orange', label: 'High' },
  medium: { color: 'warning', label: 'Medium' },
  low: { color: 'processing', label: 'Low' },
  info: { color: 'default', label: 'Info' }
};

// ── ALERT STATUS ────────────────────────────────────────────────
export const ALERT_STATUS = {
  ACTIVE: 'active',
  ACKNOWLEDGED: 'acknowledged',
  RESOLVED: 'resolved',
  SILENCED: 'silenced'
};

export const ALERT_STATUS_CONFIG = {
  active: { color: 'error', label: 'Active' },
  acknowledged: { color: 'warning', label: 'Acknowledged' },
  resolved: { color: 'success', label: 'Resolved' },
  silenced: { color: 'default', label: 'Silenced' }
};

// ── INCIDENT SEVERITY ───────────────────────────────────────────
export const INCIDENT_SEVERITY_OPTIONS = [
  { label: 'Sev 1 — System Outage', value: 'sev1', color: 'error' },
  { label: 'Sev 2 — Major Feature Broken', value: 'sev2', color: 'orange' },
  { label: 'Sev 3 — Minor Defect', value: 'sev3', color: 'warning' },
  { label: 'Sev 4 — Cosmetic', value: 'sev4', color: 'default' }
];

export const INCIDENT_SEVERITY_CONFIG = {
  sev1: { color: 'error', label: 'Sev 1' },
  sev2: { color: 'orange', label: 'Sev 2' },
  sev3: { color: 'warning', label: 'Sev 3' },
  sev4: { color: 'default', label: 'Sev 4' }
};

// ── INCIDENT STATUS ─────────────────────────────────────────────
export const INCIDENT_STATUS = {
  OPEN: 'open',
  INVESTIGATING: 'investigating',
  MITIGATED: 'mitigated',
  RESOLVED: 'resolved',
  POSTMORTEM: 'postmortem'
};

export const INCIDENT_STATUS_CONFIG = {
  open: { color: 'error', label: 'Open', icon: 'AlertOutlined' },
  investigating: { color: 'processing', label: 'Investigating', icon: 'SearchOutlined' },
  mitigated: { color: 'warning', label: 'Mitigated', icon: 'PauseCircleOutlined' },
  resolved: { color: 'success', label: 'Resolved', icon: 'CheckCircleOutlined' },
  postmortem: { color: 'purple', label: 'Postmortem', icon: 'FileTextOutlined' }
};

export const INCIDENT_STATUS_OPTIONS = [
  { label: 'Open', value: 'open' },
  { label: 'Investigating', value: 'investigating' },
  { label: 'Mitigated', value: 'mitigated' },
  { label: 'Resolved', value: 'resolved' },
  { label: 'Postmortem', value: 'postmortem' }
];

// ── LOG LEVELS ──────────────────────────────────────────────────
export const LOG_LEVELS = {
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
  FATAL: 'fatal'
};

export const LOG_LEVEL_CONFIG = {
  debug: { color: 'default', label: 'DEBUG' },
  info: { color: 'processing', label: 'INFO' },
  warn: { color: 'warning', label: 'WARN' },
  error: { color: 'error', label: 'ERROR' },
  fatal: { color: 'magenta', label: 'FATAL' }
};

// ── MONITORING WINDOW STATUS ────────────────────────────────────
export const MONITORING_WINDOW_STATUS = {
  ACTIVE: 'active',
  PASSED: 'passed',
  FAILED: 'failed',
  CANCELLED: 'cancelled'
};

export const MONITORING_WINDOW_CONFIG = {
  active: { color: 'processing', label: 'Active' },
  passed: { color: 'success', label: 'Passed' },
  failed: { color: 'error', label: 'Failed' },
  cancelled: { color: 'default', label: 'Cancelled' }
};

// ── METRIC TYPES ────────────────────────────────────────────────
export const METRIC_TYPES = {
  HTTP_REQUESTS: 'http_requests',
  ERROR_RATE: 'error_rate',
  LATENCY_P50: 'latency_p50',
  LATENCY_P95: 'latency_p95',
  LATENCY_P99: 'latency_p99',
  CPU: 'cpu',
  MEMORY: 'memory',
  CONNECTIONS: 'connections'
};

export const METRIC_TYPE_OPTIONS = [
  { label: 'HTTP Requests', value: 'http_requests' },
  { label: 'Error Rate', value: 'error_rate' },
  { label: 'Latency P50', value: 'latency_p50' },
  { label: 'Latency P95', value: 'latency_p95' },
  { label: 'Latency P99', value: 'latency_p99' },
  { label: 'CPU Usage', value: 'cpu' },
  { label: 'Memory Usage', value: 'memory' },
  { label: 'Active Connections', value: 'connections' }
];

// ── SELECT OPTIONS ──────────────────────────────────────────────
export const ALERT_CONDITION_OPTIONS = [
  { label: 'Greater Than', value: 'gt' },
  { label: 'Less Than', value: 'lt' },
  { label: 'Equal To', value: 'eq' },
  { label: 'Greater Than or Equal', value: 'gte' },
  { label: 'Less Than or Equal', value: 'lte' }
];

export const ALERT_DURATION_OPTIONS = [
  { label: '1 minute', value: '1m' },
  { label: '5 minutes', value: '5m' },
  { label: '15 minutes', value: '15m' },
  { label: '30 minutes', value: '30m' },
  { label: '1 hour', value: '1h' }
];

export const TIME_RANGE_OPTIONS = [
  { label: 'Last 15 min', value: '15m' },
  { label: 'Last 1 hour', value: '1h' },
  { label: 'Last 6 hours', value: '6h' },
  { label: 'Last 24 hours', value: '24h' },
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' }
];

export const CHANNEL_TYPES = [
  { label: 'Email', value: 'email', icon: 'MailOutlined' },
  { label: 'Slack', value: 'slack', icon: 'MessageOutlined' },
  { label: 'Webhook', value: 'webhook', icon: 'ApiOutlined' },
  { label: 'PagerDuty', value: 'pagerduty', icon: 'AlertOutlined' }
];

export const ENVIRONMENT_OPTIONS = [
  { label: 'Development', value: 'development' },
  { label: 'Staging', value: 'staging' },
  { label: 'Production', value: 'production' }
];

export const ALERT_SEVERITY_OPTIONS = [
  { label: 'Critical', value: 'critical' },
  { label: 'High', value: 'high' },
  { label: 'Medium', value: 'medium' },
  { label: 'Low', value: 'low' },
  { label: 'Info', value: 'info' }
];

// ── DEVIATION THRESHOLDS ────────────────────────────────────────
export const DEVIATION_THRESHOLDS = {
  error_rate: { factor: 2, label: 'Error rate increase > 2x' },
  latency_p95: { factor: 1.5, label: 'Latency increase > 50%' },
  http_5xx: { threshold: 0.01, label: '5xx increase > threshold' },
  rps_drop: { factor: 0.7, label: 'RPS drop > 30%' }
};

// ── TABLE COLUMN DEFINITIONS ────────────────────────────────────

export const COLUMNS_SERVICE_HEALTH = [
  { label: 'Service', id: 'serviceName', key: 'serviceName', width: 180 },
  { label: 'Version', id: 'version', key: 'version', width: 100, render: (text) => <KoText className='monoText' text={text} /> },
  { label: 'Error %', id: 'errorRate', key: 'errorRate', width: 100,
    render: (val) => (
      <KoText
        text={`${val?.toFixed(2) || '0.00'}%`}
        style={{ color: val > 5 ? 'var(--palette-error-main)' : val > 1 ? 'var(--palette-warning-main)' : 'var(--palette-success-main)' }}
      />
    )
  },
  { label: 'P95 (ms)', id: 'p95Latency', key: 'p95Latency', width: 100,
    render: (val) => (
      <KoText
        text={`${val || 0}ms`}
        style={{ color: val > 500 ? 'var(--palette-error-main)' : val > 200 ? 'var(--palette-warning-main)' : 'inherit' }}
      />
    )
  },
  { label: 'CPU %', id: 'cpuUsage', key: 'cpuUsage', width: 90,
    render: (val) => (
      <KoText
        text={`${val?.toFixed(1) || '0.0'}%`}
        style={{ color: val > 80 ? 'var(--palette-error-main)' : val > 60 ? 'var(--palette-warning-main)' : 'inherit' }}
      />
    )
  },
  { label: 'Memory %', id: 'memoryUsage', key: 'memoryUsage', width: 100,
    render: (val) => (
      <KoText
        text={`${val?.toFixed(1) || '0.0'}%`}
        style={{ color: val > 80 ? 'var(--palette-error-main)' : val > 60 ? 'var(--palette-warning-main)' : 'inherit' }}
      />
    )
  },
  { label: 'Connections', id: 'connections', key: 'connections', width: 110 },
  { label: 'Status', id: 'status', key: 'status', width: 130,
    render: (val) => {
      const cfg = HEALTH_STATUS_CONFIG[val] || HEALTH_STATUS_CONFIG.healthy;
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{
            width: '10px', height: '10px', borderRadius: '50%',
            background: cfg.dotColor, display: 'inline-block'
          }} />
          <KoTag color={cfg.color}>{cfg.label}</KoTag>
        </div>
      );
    }
  }
];

export const COLUMNS_ALERTS = [
  { label: 'Name', id: 'name', key: 'name', width: 200 },
  { label: 'Severity', id: 'severity', key: 'severity', width: 110,
    render: (val) => {
      const cfg = ALERT_SEVERITY_CONFIG[val] || {};
      return <KoTag color={cfg.color}>{cfg.label || val}</KoTag>;
    }
  },
  { label: 'Metric', id: 'metric', key: 'metric', width: 140 },
  { label: 'Condition', id: 'condition', key: 'condition', width: 100 },
  { label: 'Threshold', id: 'threshold', key: 'threshold', width: 100 },
  { label: 'Service', id: 'service', key: 'service', width: 150 },
  { label: 'Status', id: 'status', key: 'status', width: 110,
    render: (val) => {
      const cfg = ALERT_STATUS_CONFIG[val] || {};
      return <KoTag color={cfg.color}>{cfg.label || val}</KoTag>;
    }
  },
  { label: 'Last Triggered', id: 'lastTriggered', key: 'lastTriggered', width: 160,
    render: (val) => val ? <KoDateFormatter data={val} /> : <KoText text='—' />
  }
];

export const COLUMNS_ALERT_HISTORY = [
  { label: 'Timestamp', id: 'timestamp', key: 'timestamp', width: 170,
    render: (val) => <KoDateFormatter data={val} />
  },
  { label: 'Alert Name', id: 'alertName', key: 'alertName', width: 200 },
  { label: 'Severity', id: 'severity', key: 'severity', width: 110,
    render: (val) => {
      const cfg = ALERT_SEVERITY_CONFIG[val] || {};
      return <KoTag color={cfg.color}>{cfg.label || val}</KoTag>;
    }
  },
  { label: 'Service', id: 'service', key: 'service', width: 150 },
  { label: 'Value', id: 'metricValue', key: 'metricValue', width: 100 },
  { label: 'Status', id: 'status', key: 'status', width: 110,
    render: (val) => {
      const cfg = ALERT_STATUS_CONFIG[val] || {};
      return <KoTag color={cfg.color}>{cfg.label || val}</KoTag>;
    }
  },
  { label: 'Duration', id: 'duration', key: 'duration', width: 100 }
];

export const COLUMNS_INCIDENTS = [
  { label: 'ID', id: 'incidentId', key: 'incidentId', width: 120,
    render: (text) => <KoText className='monoText' text={text} />
  },
  { label: 'Title', id: 'title', key: 'title' },
  { label: 'Severity', id: 'severity', key: 'severity', width: 100,
    render: (val) => {
      const cfg = INCIDENT_SEVERITY_CONFIG[val] || {};
      return <KoTag color={cfg.color}>{cfg.label || val}</KoTag>;
    }
  },
  { label: 'Status', id: 'status', key: 'status', width: 130,
    render: (val) => {
      const cfg = INCIDENT_STATUS_CONFIG[val] || {};
      return <KoTag color={cfg.color}>{cfg.label || val}</KoTag>;
    }
  },
  { label: 'Owner', id: 'owner', key: 'owner', width: 140 },
  { label: 'Service', id: 'service', key: 'service', width: 150 },
  { label: 'MTTR', id: 'mttr', key: 'mttr', width: 100,
    render: (val) => <KoText text={val ? `${val}` : '—'} />
  },
  { label: 'Created', id: 'createdAt', key: 'createdAt', width: 160,
    render: (val) => <KoDateFormatter data={val} />
  },
  { label: 'Release', id: 'linkedRelease', key: 'linkedRelease', width: 120 }
];

export const COLUMNS_LOGS = [
  { label: 'Timestamp', id: 'timestamp', key: 'timestamp', width: 190,
    render: (val) => <KoText className='monoText' text={val} />
  },
  { label: 'Level', id: 'level', key: 'level', width: 90,
    render: (val) => {
      const cfg = LOG_LEVEL_CONFIG[val] || {};
      return <KoTag color={cfg.color}>{cfg.label || val}</KoTag>;
    }
  },
  { label: 'Service', id: 'service', key: 'service', width: 150 },
  { label: 'Message', id: 'message', key: 'message',
    render: (text) => <KoText text={text} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '400px', display: 'block' }} />
  },
  { label: 'Trace ID', id: 'traceId', key: 'traceId', width: 160,
    render: (text) => <KoText className='monoText' text={text || '—'} />
  }
];

export const COLUMNS_RELEASE_MONITORING = [
  { label: 'Release', id: 'releaseName', key: 'releaseName', width: 150 },
  { label: 'Service', id: 'service', key: 'service', width: 150 },
  { label: 'Metric', id: 'metric', key: 'metric', width: 140 },
  { label: 'Pre-Deploy', id: 'preValue', key: 'preValue', width: 110 },
  { label: 'Post-Deploy', id: 'postValue', key: 'postValue', width: 110 },
  { label: 'Deviation %', id: 'deviation', key: 'deviation', width: 120,
    render: (val) => (
      <KoText
        text={`${val > 0 ? '+' : ''}${val?.toFixed(1) || '0.0'}%`}
        style={{ color: Math.abs(val) > 50 ? 'var(--palette-error-main)' : Math.abs(val) > 20 ? 'var(--palette-warning-main)' : 'var(--palette-success-main)', fontWeight: 600 }}
      />
    )
  },
  { label: 'Status', id: 'status', key: 'status', width: 120,
    render: (val) => {
      const cfg = MONITORING_WINDOW_CONFIG[val] || {};
      return <KoTag color={cfg.color}>{cfg.label || val}</KoTag>;
    }
  }
];
